import { PublicKey, Keypair } from '@solana/web3.js';

export interface SolanaTokenPayload {
  address: string;
  blockchain: 'solana';
  timestamp: number;
  expiresAt: number;
}

export async function createSolanaToken(
  wallet: any, // Phantom wallet
  expireTime = '30d',
): Promise<string> {
  try {
    if (!wallet || !wallet.publicKey) {
      throw new Error('Phantom wallet not connected');
    }

    const address = wallet.publicKey.toString();
    const timestamp = Math.floor(Date.now() / 1000);

    // Calculate expiration time
    const expireSeconds = parseExpireTime(expireTime);
    const expiresAt = timestamp + expireSeconds;

    // Create payload
    const payload: SolanaTokenPayload = {
      address,
      blockchain: 'solana',
      timestamp,
      expiresAt,
    };

    // Create message to sign
    const message = `Sign this message to authenticate with LixiNFT: ${JSON.stringify(
      payload,
    )}`;
    const encodedMessage = new TextEncoder().encode(message);

    // Sign with Phantom wallet
    const signature = await wallet.signMessage(encodedMessage);

    console.log('Solana signature debug:', {
      signature: signature,
      signatureType: typeof signature,
      signatureLength: signature?.length,
      isUint8Array: signature instanceof Uint8Array,
      signatureKeys: signature ? Object.keys(signature) : [],
      signatureValues: signature ? Object.values(signature) : [],
    });

    // Convert signature to base64 - handle different signature formats
    let signatureString: string;

    if (signature instanceof Uint8Array) {
      // Standard Uint8Array signature
      const signatureArray = Array.from(signature) as number[];
      signatureString = btoa(String.fromCharCode(...signatureArray));
    } else if (typeof signature === 'string') {
      // String signature (hex or base64)
      if (signature.startsWith('0x')) {
        // Hex string - convert to base64
        const hex = signature.slice(2);
        const bytes: number[] = [];
        for (let i = 0; i < hex.length; i += 2) {
          bytes.push(parseInt(hex.substr(i, 2), 16));
        }
        signatureString = btoa(String.fromCharCode(...bytes));
      } else {
        // Assume it's already base64
        signatureString = signature;
      }
    } else if (signature && typeof signature.signature === 'string') {
      // Phantom might return { signature: string }
      signatureString = signature.signature;
    } else if (signature && typeof signature === 'object') {
      // Handle Phantom wallet object signature
      if (signature.signature && typeof signature.signature === 'string') {
        signatureString = signature.signature;
      } else if (
        signature.signature &&
        signature.signature instanceof Uint8Array
      ) {
        // Handle { signature: Uint8Array(64), publicKey: string }
        signatureString = btoa(String.fromCharCode(...signature.signature));
      } else if (
        signature.signature &&
        signature.signature.data &&
        Array.isArray(signature.signature.data)
      ) {
        // Handle { signature: { type: "Buffer", data: [number[]] } }
        signatureString = btoa(
          String.fromCharCode(...signature.signature.data),
        );
      } else if (signature.data && Array.isArray(signature.data)) {
        // Convert array of numbers to base64
        signatureString = btoa(String.fromCharCode(...signature.data));
      } else if (signature.signature && Array.isArray(signature.signature)) {
        // Convert array of numbers to base64
        signatureString = btoa(String.fromCharCode(...signature.signature));
      } else {
        console.error('Unknown object signature format:', signature);
        throw new Error(
          `Unsupported object signature format: ${JSON.stringify(signature)}`,
        );
      }
    } else {
      throw new Error(`Unsupported signature format: ${typeof signature}`);
    }

    console.log('Solana signature conversion:', {
      originalSignature: signature,
      signatureString: signatureString
        ? signatureString.substring(0, 20) + '...'
        : 'UNDEFINED',
      signatureStringLength: signatureString?.length || 0,
      signatureStringType: typeof signatureString,
    });

    // Validate signatureString before creating token
    if (!signatureString || signatureString.length === 0) {
      throw new Error('Signature string is empty or undefined');
    }

    // Create token with blockchain header
    const payloadBase64 = btoa(JSON.stringify(payload));
    const token = `solana.${payloadBase64}.${signatureString}`;

    console.log('Solana token created:', {
      address: address.substring(0, 8) + '...',
      blockchain: 'solana',
      payloadBase64: payloadBase64.substring(0, 20) + '...',
      signatureString: signatureString.substring(0, 20) + '...',
      tokenLength: token.length,
      tokenPreview: token.substring(0, 100) + '...',
      tokenParts: token.split('.'),
      tokenPartsCount: token.split('.').length,
      hasSignature: token.includes('.') && token.split('.').length === 3,
    });

    return token;
  } catch (error) {
    console.error('Create Solana token error:', error);
    throw new Error(
      `Failed to create Solana token: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    );
  }
}

function parseExpireTime(expireTime: string): number {
  const timeUnits: { [key: string]: number } = {
    s: 1,
    m: 60,
    h: 3600,
    d: 86400,
    w: 604800,
    M: 2592000, // 30 days
    y: 31536000,
  };

  const match = expireTime.match(/^(\d+)([smhdwMy])$/);
  if (!match) {
    throw new Error(`Invalid expire time format: ${expireTime}`);
  }

  const value = parseInt(match[1]);
  const unit = match[2];

  return value * timeUnits[unit];
}
